import { useSearch } from "@sinch/hooks/utilities/useSearch";
import { useWatchDependency } from "@sinch/hooks/utilities/useWatchDependency";
import { isEmpty, length, max, min, range } from "ramda";
import { isArray, isFunction, isNotNil, isNotNilOrEmpty } from "ramda-adjunct";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  flatOptions,
  getDepth,
  getLeafList,
  searchPath,
} from "@sinch/components/form/MultilevelSelect/hooks/optionTreeTools";
import { useAsyncLoadChildren } from "@sinch/components/form/MultilevelSelect/hooks/useAsyncLoadChildren";
import { useFocusPath } from "@sinch/components/form/MultilevelSelect/hooks/useFocusPath";
import { ListColumnProps, NestedOptions } from "@sinch/components/form/MultilevelSelect/types";

/**
 * Custom hook to manage state and logic for a multilevel select component.
 * Supports nested options, search functionality, and dynamic level rendering.
 */
export const useMultilevelSelect = <TValue,>({
  options,
  onChange,
  value,
}: {
  onChange?: (e: React.UIEvent<HTMLElement>, val: TValue) => void;
  options: NestedOptions<TValue>[];
  value: TValue | null;
}) => {
  const pathByValue = useMemo(() => (isNotNil(value) ? (searchPath(options, value) ?? []) : []), [value, options]);
  // State to track the selected path in the nested options
  const [selectedPath, setSelectedPath] = useState<number[]>(pathByValue);
  // State to track the search query
  const [search, setSearch] = useState<string>("");

  // Memoized flattened options for search functionality
  const structuredOptions = useMemo(
    () => (isNotNilOrEmpty(search) ? flatOptions(options) : options),
    [options, search]
  );

  const { memoizeLoading, loading, error } = useAsyncLoadChildren();

  // Search function to filter options based on the search query
  const searchFn = useSearch<NestedOptions<TValue>>(structuredOptions, [["label"]]);
  // Search results or original options if no search query is present
  const searchResult = isNotNilOrEmpty(search) ? searchFn(search) : options;
  /**
   * Retrieves the list of options for a specific level based on the selected path.
   */
  const getLevelList = (level: number): NestedOptions<TValue>[] | Promise<void> => {
    if ((length(selectedPath) <= 1 && level === 0) || level === 0) {
      return searchResult;
    }
    if (level > selectedPath.length) {
      return [];
    }
    return (length(selectedPath) <= 1 && level === 0) || level === 0
      ? searchResult
      : (selectedPath.slice(0, level).reduce<NestedOptions<TValue>[] | Promise<void> | null>((acc, index) => {
          const previousVal = acc ?? searchResult;
          if (isArray(previousVal)) {
            const children = previousVal[index]?.children ?? [];
            return isArray(children) ? children : memoizeLoading(index, children);
          }
          return previousVal;
        }, null) ?? []);
  };

  /**
   * Handles selection of an option at a specific level.
   */
  const handleSelect = useCallback((level: number, index: number) => {
    setSelectedPath((prev) => [...prev.slice(0, level), index]);
  }, []);

  /**
   * Handles changes to the search query.
   */
  const handleSearch = (val: string) => {
    if (length(val) >= 3) {
      setSelectedPath([]);
    }
    setSearch(val);
    if (isEmpty(val)) {
      // Reset to the first option if the search query is cleared
      handleSelect(0, 0);
    } else {
      // Clear the selected path when searching
      setSelectedPath([]);
    }
  };

  useWatchDependency(() => {
    setSelectedPath(pathByValue);
  }, [value]);

  useEffect(() => {
    if (length(search) >= 3) {
      getLeafList(options)?.forEach((item, index) => {
        if (isFunction(item.children)) {
          memoizeLoading(index, item.children);
        }
      });
    }
  }, [search, options]);

  const currentDepth = getDepth(searchResult);
  const focusPath = useFocusPath({ initialPath: Array(currentDepth).fill(null) });

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>, level: number, ix: number) => {
      const list = getLevelList(level);
      if (!isArray(list)) {
        return;
      }

      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "ArrowLeft" || e.key === "ArrowRight") {
        // Prevent the browser from scrolling the page/container
        e.preventDefault();
      }

      const currentIdx = focusPath.path[ix] ?? 0;

      if (e.key === "ArrowDown") {
        const nextIndex = min(list.length - 1, currentIdx + 1);
        focusPath.setAt(ix, nextIndex);
        handleSelect(level, nextIndex);
      }
      if (e.key === "ArrowUp") {
        const previousIndex = max(0, currentIdx - 1);
        focusPath.setAt(ix, previousIndex);
        handleSelect(level, previousIndex);
      }
      if (e.key === "ArrowLeft" && ix > 0) {
        focusPath.setAt(ix, null);
      }
      if (e.key === "ArrowRight" && ix < currentDepth - 1 && isNotNilOrEmpty(list[currentIdx]?.children)) {
        focusPath.setAt(ix + 1, 0);
      }
    },
    [currentDepth, focusPath, getLevelList, handleSelect]
  );

  // Generate columns for each level with their respective options and event handlers
  const listColumn: ListColumnProps<TValue>[] = useMemo(
    () =>
      range(0, currentDepth).map((level) => ({
        level,
        list: getLevelList(level) ?? [],
        onOpenList: (index: number) => handleSelect(level, index),
        onChange: onChange,
        onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>, ix: number) => handleKeyDown(e, level, ix),
        selected: selectedPath[level] ?? null,
        atValuePath: pathByValue[level] ?? null,
      })),
    [currentDepth, getLevelList, handleKeyDown, handleSelect, onChange, pathByValue, selectedPath]
  );

  return {
    listColumn,
    search,
    onSearch: handleSearch,
    loading,
    selectedPath,
    error,
    focusPath,
  };
};
